import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Check, Sparkles } from 'lucide-react';
import {
  DEFAULT_TRANSITION_OUT,
  defaultDurationForTransition,
  getTransitionById,
  normalizeTransitionOut,
  transitionsByCategory,
  TRANSITION_CATEGORIES,
  VIDEO_TRANSITIONS,
} from '@/lib/videoTransitions';

/**
 * Popup to pick a transition between two timeline scene segments.
 * Shows effect grid + a mini scene strip with the junction highlighted.
 */
export function TransitionPickerDialog({
  open,
  onClose,
  fromClip,
  toClip,
  initialTransitionOut,
  initialDuration,
  onApply,
}) {
  const [transitionOut, setTransitionOut] = useState(
    normalizeTransitionOut(initialTransitionOut || DEFAULT_TRANSITION_OUT),
  );
  const [duration, setDuration] = useState(
    Number(initialDuration) || defaultDurationForTransition(initialTransitionOut),
  );
  const [categoryId, setCategoryId] = useState('basic');
  const grouped = useMemo(() => transitionsByCategory(), []);

  useEffect(() => {
    if (!open) return;
    const next = normalizeTransitionOut(initialTransitionOut || DEFAULT_TRANSITION_OUT);
    setTransitionOut(next);
    setDuration(
      next === 'cut'
        ? 0
        : Math.max(0.1, Number(initialDuration) || defaultDurationForTransition(next)),
    );
    const meta = getTransitionById(next);
    if (meta?.category) setCategoryId(meta.category);
  }, [open, initialTransitionOut, initialDuration, fromClip?.id, toClip?.id]);

  const items = grouped[categoryId] || VIDEO_TRANSITIONS.filter((t) => t.category === categoryId);
  const selected = getTransitionById(transitionOut);

  const apply = () => {
    onApply?.({
      transitionOut: normalizeTransitionOut(transitionOut),
      transitionDuration: transitionOut === 'cut'
        ? 0
        : Math.max(0.1, Math.min(2, Number(duration) || 0.5)),
    });
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl gap-0 p-0 overflow-hidden"
        style={{ background: 'var(--bg-primary)', border: '1px solid var(--glass-border)' }}
        data-testid="transition-picker-dialog"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <DialogTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
            <Sparkles className="w-4 h-4 text-amber-300" />
            Scene transition
          </DialogTitle>
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            Choose how this scene blends into the next. Applied on the outgoing clip.
          </p>
        </DialogHeader>

        {/* Mini scene timeline */}
        <div
          className="px-5 py-3 border-b flex items-center gap-2"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}
          data-testid="transition-picker-scene-strip"
        >
          <div
            className="flex-1 min-w-0 rounded-lg px-3 py-2 text-xs font-semibold truncate border"
            style={{ background: '#f59e0b33', borderColor: '#f59e0b66', color: '#fde68a' }}
            title={fromClip?.name || 'Scene A'}
          >
            {fromClip?.name || 'Outgoing scene'}
          </div>
          <div
            className="shrink-0 rounded-full border-2 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
            style={{
              borderColor: transitionOut === 'cut' ? 'var(--glass-border)' : '#fbbf24',
              color: transitionOut === 'cut' ? 'var(--text-tertiary)' : '#fde68a',
              background: transitionOut === 'cut' ? 'transparent' : '#fbbf2422',
            }}
          >
            {selected?.label || 'Cut'}
            {transitionOut !== 'cut' ? ` · ${Number(duration).toFixed(1)}s` : ''}
          </div>
          <div
            className="flex-1 min-w-0 rounded-lg px-3 py-2 text-xs font-semibold truncate border"
            style={{ background: '#8b5cf633', borderColor: '#8b5cf666', color: '#ddd6fe' }}
            title={toClip?.name || 'Scene B'}
          >
            {toClip?.name || 'Incoming scene'}
          </div>
        </div>

        {/* Category tabs */}
        <div className="px-4 pt-3 flex gap-1 overflow-x-auto">
          {TRANSITION_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategoryId(cat.id)}
              className="shrink-0 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide transition"
              style={{
                background: categoryId === cat.id ? 'var(--primary)' : 'var(--bg-tertiary)',
                color: categoryId === cat.id ? '#fff' : 'var(--text-secondary)',
              }}
              data-testid={`transition-cat-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Effect grid */}
        <div className="px-4 py-3 max-h-[min(42vh,320px)] overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {items.map((t) => {
              const active = transitionOut === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setTransitionOut(t.id);
                    if (t.id === 'cut') setDuration(0);
                    else setDuration(defaultDurationForTransition(t.id));
                  }}
                  className="relative text-left rounded-xl border p-2.5 transition hover:brightness-110"
                  style={{
                    borderColor: active ? '#fbbf24' : 'var(--glass-border)',
                    background: active ? '#fbbf2418' : 'var(--bg-secondary)',
                    boxShadow: active ? '0 0 0 1px #fbbf24' : 'none',
                  }}
                  data-testid={`transition-option-${t.id}`}
                >
                  {active ? (
                    <Check className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-amber-300" />
                  ) : null}
                  <div className="text-xs font-semibold pr-4" style={{ color: 'var(--text-primary)' }}>
                    {t.label}
                  </div>
                  {t.useCase ? (
                    <div className="text-[10px] mt-0.5 line-clamp-2" style={{ color: 'var(--text-tertiary)' }}>
                      {t.useCase}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {transitionOut !== 'cut' ? (
          <div className="px-5 pb-2 space-y-2">
            <div className="flex justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
              <span>Duration</span>
              <span className="tabular-nums">{Number(duration).toFixed(1)}s</span>
            </div>
            <Slider
              value={[Math.round(Number(duration) * 10) || 5]}
              min={1}
              max={20}
              step={1}
              onValueChange={([v]) => setDuration(v / 10)}
            />
          </div>
        ) : null}

        <div
          className="px-5 py-3 border-t flex justify-end gap-2"
          style={{ borderColor: 'var(--glass-border)' }}
        >
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={apply} data-testid="transition-picker-apply">
            Apply transition
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TransitionPickerDialog;
